import ArrowUpRight from "@uui/ArrowUpRight";
import CheckCircle from "@uui/CheckCircle";
import XCircle from "@uui/XCircle";

import {
  Button,
  ButtonProps,
  Chip as MuiChip,
  Radio,
  RadioProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import Link from "@rui/Link";
import Stack, { Row, StackProps } from "@rui/Stack";

type SwitchLabelProps = TypographyProps & { selected?: boolean };

export const SwitchLabel = styled(
  ({ selected: _, ...props }: SwitchLabelProps) => (
    <Typography variant="bodySecondaryMed" {...props} />
  )
)(({ selected, theme }) => ({
  color: selected
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[500],
}));

type PlanDetailsProps = StackProps & { selected: boolean };

export const PlanDetails = styled(
  ({ selected: _, ...props }: PlanDetailsProps) => <Stack {...props} />
)(({ onClick, selected, theme }) => ({
  padding: "10px 12px",
  flexGrow: 1,
  [theme.breakpoints.up("md")]: {
    maxWidth: "calc(50% - 8px)",
  },
  borderRadius: "8px",
  border: "2px solid",
  borderColor: selected
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[100],
  cursor: onClick ? "pointer" : "default",
}));

export const PlanName = styled((props: TypographyProps) => (
  <Typography variant="h3" {...props} />
))(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
}));

export const PlanRadio = styled((props: RadioProps) => (
  <Radio disableRipple {...props} />
))(({ checked, theme }) => ({
  padding: 0,
  "& svg": {
    color: checked
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.neutral[300],
  },
}));

export const PlanDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.shades.neutral[500],
}));

type FeatureProps = StackProps & { supported?: boolean };

export const Feature = styled(
  ({ children, supported, ...props }: FeatureProps) => (
    <Row variant="left" spacing={1} {...props}>
      {supported ? (
        <CheckCircle sx={{ color: "shades.meadow.500" }} />
      ) : (
        <XCircle sx={{ color: "shades.negative.500" }} />
      )}
      <Typography variant="bodySecondaryReg">{children}</Typography>
    </Row>
  )
)({
  "& svg": {
    height: "12px",
    width: "12px",
  },
});

export const DisclaimerLink = styled(({ href, ...props }: ButtonProps) => (
  <Link variant="labelReg" href={href}>
    <Button {...props} endIcon={<ArrowUpRight />} />
  </Link>
))(({ theme }) => ({
  padding: 0,
  paddingLeft: "4px",
  fontSize: "0.75rem",
  textDecoration: "underline",
  color: theme.palette.shades.neutral[400],
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
    color: theme.palette.shades.secondary[500],
  },
  "& .MuiButton-endIcon": {
    marginLeft: "4px",
  },
  "& svg": {
    height: "16px",
    width: "16px",
  },
}));

export const Chip = styled(MuiChip)({
  textTransform: "uppercase",
  padding: "0 4px",
  fontSize: "0.625rem",
  minHeight: 0,
  height: "auto",
  minWidth: 0,
  width: "auto",
  borderRadius: "4px",
  cursor: "inherit",
  "& .MuiChip-label": { padding: 0 },
});

export const Pricing = styled(Stack)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.shades.neutral[100]}`,
  width: "100%",
  paddingTop: theme.spacing(2),
}));
